import "reflect-metadata";
import "./ui/index.less";
import { di } from "@dp-instant/core";
import { Localize } from "@dp-instant/i18n";
import { GameModuleBase, startGame } from "@dp-instant/game-template";
import { GameModule } from "./GameModule";
import { TelegramSocialModule } from "./social/tg/TelegramSocialModule";
import { Playdeck } from "./coregame/utils/Playdeck";
import { apiClient } from "./api/ApiClient";
import { translations } from "./i18n/translations";
import { Howler } from "howler";

// Telegram WebApp handle — used for locale detection and lifecycle below
const tg = (window as any).Telegram?.WebApp;

// Load i18n translations; override locale from saved preference or TG language
Localize.addTemplates(translations);
const savedLang = localStorage.getItem("sio_lang");
if (savedLang) {
    Localize.defaultLocale = savedLang;
} else {
    const langCode = tg?.initDataUnsafe?.user?.language_code;
    if (langCode?.startsWith("ru")) {
        Localize.defaultLocale = "ru_RU";
    }
}

// Call tg.ready() FIRST — dismisses Telegram white loading overlay immediately
if (tg) {
    tg.ready();
    tg.expand();
    if (tg.disableVerticalSwipes) {
        tg.disableVerticalSwipes();
    }
    // Request fullscreen by default (user can disable in settings)
    if (localStorage.getItem("sio_fullscreen") !== "0") {
        try { tg.requestFullscreen(); } catch (e) { /* unsupported client */ }
    }

    // Safe area CSS variables: device inset (notch) + TG content inset (header buttons)
    const applySafeArea = () => {
        const sa = tg.safeAreaInset || { top: 0, bottom: 0, left: 0, right: 0 };
        const csa = tg.contentSafeAreaInset || { top: 0, bottom: 0, left: 0, right: 0 };
        const root = document.documentElement.style;
        root.setProperty("--sio-safe-top", (sa.top + csa.top) + "px");
        root.setProperty("--sio-safe-bottom", (sa.bottom + csa.bottom) + "px");
        root.setProperty("--sio-safe-left", (sa.left + csa.left) + "px");
        root.setProperty("--sio-safe-right", (sa.right + csa.right) + "px");
        root.setProperty("--sio-device-safe-top", sa.top + "px");
        root.setProperty("--sio-content-safe-top", csa.top + "px");
    };
    applySafeArea();
    tg.onEvent("safeAreaChanged", applySafeArea);
    tg.onEvent("contentSafeAreaChanged", applySafeArea);
}

// Unlock Howler AudioContext on first user gesture (required by mobile TG WebView)
const unlockAudio = () => {
    if (Howler.ctx && Howler.ctx.state === "suspended") {
        Howler.ctx.resume();
    }
    document.removeEventListener("touchstart", unlockAudio);
    document.removeEventListener("click", unlockAudio);
};
document.addEventListener("touchstart", unlockAudio, {passive: true});
document.addEventListener("click", unlockAudio);

const showTgError = (err: unknown) => {
    const msg = err instanceof Error ? err.message + "\n" + err.stack : String(err);
    console.error("[TG] Fatal error:", msg);
    if (tg?.showAlert) {
        tg.showAlert("Game init error:\n" + msg.slice(0, 300));
    }
};

try {
    // Patch Playdeck to use localStorage instead of PlayDeck postMessage iframe API
    const SAVE_KEY = "sio_cookie_tg";

    // Build sync payload from saved data string
    const buildSyncPayload = (value: string) => {
        const data = JSON.parse(value);
        return {
            coins: data.coins,
            absoluteLevelNum: data.absoluteLevelNum,
            absoluteTryNum: data.absoluteTryNum,
            currentStage: data.currentStage,
            wins: data.wins,
            loses: data.loses,
            fightsPassed: data.fightsPassed,
            botLevel: data.bot_level,
            spawnLevel: data.playerSpawnLevel,
            startPopLevel: data.playerStartPopulation,
            offlineLevel: data.playerOfflineLevel,
            selectedColor: data.selected_color_set_id,
            selectedBldg: data.selected_building_id,
            selectedFtr: data.selected_fighter_id,
            userBuildingsSet: JSON.stringify(data.user_buildings_set || [1]),
            userFighterSet: JSON.stringify(data.user_fighter_set || [1]),
        };
    };

    // Debounced sync to server (must be declared before setData which uses it)
    let syncTimer: ReturnType<typeof setTimeout> | null = null;
    const debouncedSyncToServer = (value: string) => {
        if (syncTimer) clearTimeout(syncTimer);
        syncTimer = setTimeout(() => {
            try {
                apiClient.syncProgress(buildSyncPayload(value)).catch(err => console.warn("[TG] Sync failed:", err));
            } catch (e) {
                console.warn("[TG] Sync parse error:", e);
            }
        }, 500);
    };

    // Immediate sync (for level end events)
    const immediateSyncToServer = () => {
        try {
            const saved = localStorage.getItem(SAVE_KEY);
            if (saved) {
                apiClient.syncProgress(buildSyncPayload(saved)).catch(err => console.warn("[TG] Immediate sync failed:", err));
            }
        } catch (e) {
            console.warn("[TG] Immediate sync error:", e);
        }
    };

    // Immediate flush for beforeunload/visibilitychange
    const flushSyncNow = () => {
        if (syncTimer) {
            clearTimeout(syncTimer);
            syncTimer = null;
        }
        try {
            const saved = localStorage.getItem(SAVE_KEY);
            if (saved) {
                const payload = buildSyncPayload(saved);
                const initData = (window as any).Telegram?.WebApp?.initData || "";
                // fetch with keepalive for reliability on page close
                fetch(apiClient.baseUrl + "/api/player/sync", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `tma ${initData}`,
                    },
                    body: JSON.stringify(payload),
                    keepalive: true,
                }).catch(() => {});
            }
        } catch {}
    };

    window.addEventListener("beforeunload", flushSyncNow);
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") flushSyncNow();
    });

    // Hydrate CookieModel from server profile — merge local + server, keep best progress
    const hydrateFromServer = (profile: any) => {
        try {
            const saved = localStorage.getItem(SAVE_KEY);
            const local = saved ? JSON.parse(saved) : {};

            // Check if account was wiped — server wipedAt is newer than locally stored one
            const serverWipedAt = profile.wipedAt ? new Date(profile.wipedAt).getTime() : 0;
            const localWipedAt = local._lastWipedAt ? new Date(local._lastWipedAt).getTime() : 0;
            const wasWiped = serverWipedAt > 0 && serverWipedAt > localWipedAt;

            if (wasWiped) {
                // Server was wiped — overwrite everything with server values, ignore local
                console.log("[TG] Account wipe detected, resetting local state");
                local.coins = profile.coins ?? 0;
                local.absoluteLevelNum = profile.absoluteLevelNum ?? 1;
                local.absoluteTryNum = profile.absoluteTryNum ?? 0;
                local.currentStage = profile.currentStage ?? 0;
                local.wins = profile.wins ?? 0;
                local.loses = profile.loses ?? 0;
                local.fightsPassed = profile.fightsPassed ?? 0;
                local.bot_level = profile.botLevel ?? 0;
                local.playerSpawnLevel = profile.spawnLevel ?? 1;
                local.playerStartPopulation = profile.startPopLevel ?? 1;
                local.playerOfflineLevel = profile.offlineLevel ?? 1;
                local.lastLaunch = 0;
                local._lastWipedAt = profile.wipedAt;
            } else {
                // Normal merge — use Math.max for progressive fields
                local.coins = local.coins ?? profile.coins ?? 0; // Client is source of truth for coins
                local.absoluteLevelNum = Math.max(profile.absoluteLevelNum ?? 1, local.absoluteLevelNum ?? 1);
                local.absoluteTryNum = Math.max(profile.absoluteTryNum ?? 0, local.absoluteTryNum ?? 0);
                local.currentStage = Math.max(profile.currentStage ?? 0, local.currentStage ?? 0);
                local.wins = Math.max(profile.wins ?? 0, local.wins ?? 0);
                local.loses = Math.max(profile.loses ?? 0, local.loses ?? 0);
                local.fightsPassed = Math.max(profile.fightsPassed ?? 0, local.fightsPassed ?? 0);
                local.bot_level = Math.max(profile.botLevel ?? 0, local.bot_level ?? 0);
                local.playerSpawnLevel = Math.max(profile.spawnLevel ?? 1, local.playerSpawnLevel ?? 1);
                local.playerStartPopulation = Math.max(profile.startPopLevel ?? 1, local.playerStartPopulation ?? 1);
                local.playerOfflineLevel = Math.max(profile.offlineLevel ?? 1, local.playerOfflineLevel ?? 1);
            }
            // Selection fields — server wins (not progressive, just preference)
            local.selected_color_set_id = profile.selectedColor ?? local.selected_color_set_id ?? "BLUE";
            local.selected_building_id = profile.selectedBldg ?? local.selected_building_id ?? 1;
            local.selected_fighter_id = profile.selectedFtr ?? local.selected_fighter_id ?? 1;
            if (profile.userBuildingsSet) {
                try { local.user_buildings_set = JSON.parse(profile.userBuildingsSet); } catch {}
            }
            if (profile.userFighterSet) {
                try { local.user_fighter_set = JSON.parse(profile.userFighterSet); } catch {}
            }
            localStorage.setItem(SAVE_KEY, JSON.stringify(local));
        } catch (e) {
            console.warn("[TG] Hydrate failed:", e);
        }
    };

    (Playdeck as any).getData = async (_key: string) => {
        try {
            return localStorage.getItem(SAVE_KEY);
        } catch (e) {
            console.warn("[TG] localStorage.getItem failed:", e);
            return null;
        }
    };
    (Playdeck as any).setData = async (_key: string, value: string) => {
        try {
            localStorage.setItem(SAVE_KEY, value);
            debouncedSyncToServer(value);
        } catch (e) {
            console.warn("[TG] localStorage.setItem failed (quota?):", e);
        }
    };
    (Playdeck as any).getProfile = async () => {
        const tgUser = (window as any).Telegram?.WebApp?.initDataUnsafe?.user;
        return {
            avatar: tgUser?.photo_url || "",
            name: tgUser?.first_name || "Player",
        };
    };
    (Playdeck as any).sendProgress = () => {};
    (Playdeck as any).loading = () => {};

    // showAd — no ads in TG, always return false (ad not watched)
    (Playdeck as any).showAd = async () => false;

    // Expose immediate sync for level end events
    (window as any).__tgImmediateSync = immediateSyncToServer;

    // requestPayment — route through hybrid balance/Stars purchase flow
    (Playdeck as any).requestPayment = async (description: string, _amount: number, _externalId: string) => {
        const slugMap: Record<string, string> = {
            "no ads": "no_ads_month",
        };
        const slug = slugMap[description] || "no_ads_month";
        try {
            const {paid} = await apiClient.purchaseFlow(slug);
            return {status: paid ? "paid" : "cancelled"};
        } catch (e) {
            console.error("[TG] requestPayment failed:", e);
            (window as any).Telegram?.WebApp?.showAlert?.("Payment error. Please try again.");
            return {status: "cancelled"};
        }
    };

    // Load DI modules: TelegramSocialModule MUST come before GameModuleBase
    // because GameModuleBase internally resolves social.cookie during loading
    di.load(TelegramSocialModule);
    di.load(GameModuleBase);
    di.load(GameModule);

    const removeLoader = () => {
        const loader = document.getElementById("sio-loader");
        if (loader) loader.remove();
    };

    // Parse color choice from /start button (before first battle only)
    const tgStartParam = tg?.initDataUnsafe?.start_param || "";
    if (tgStartParam.startsWith("color_")) {
        const saved = localStorage.getItem(SAVE_KEY);
        const local = saved ? JSON.parse(saved) : {};
        if (!local.absoluteTryNum || local.absoluteTryNum === 0) {
            const color = tgStartParam === "color_red" ? "RED" : "BLUE";
            local.selected_color_set_id = color;
            localStorage.setItem(SAVE_KEY, JSON.stringify(local));
            console.log("[TG] Color from /start:", color);
        }
    }

    // Fetch remote config in parallel with auth — both must complete before game start
    const configPromise = apiClient.getConfig().then(() => {
        // Expose AB config to coregame via globals (avoids import issues)
        const botUpgradeSeq = apiClient.configValue("early_bot_upgrade", null) as number[] | null;
        if (botUpgradeSeq) (window as any).__earlyBotUpgrade = botUpgradeSeq;
    }).catch(err => console.warn("[TG] Config fetch failed:", err));

    // Flush events on page close
    window.addEventListener("beforeunload", () => apiClient.flushEventsSync());
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") apiClient.flushEventsSync();
    });

    // Auth + config both blocking — wait for both before starting the game
    const authPromise = apiClient.authPlayer().then(profile => {
        if (profile) {
            hydrateFromServer(profile);
            // Store referral gift flag for showing after first battle
            if ((profile as any).referralGiftPending) {
                (window as any).__tgReferralGiftPending = true;
            }
            // Store daily leaderboard reward for showing on start screen
            if ((profile as any).pendingDailyReward) {
                (window as any).__tgPendingDailyReward = (profile as any).pendingDailyReward;
            }
        }
        // Request write access for bot messaging (fire-and-forget)
        if (tg?.requestWriteAccess) {
            tg.requestWriteAccess(() => {});
        }
        // Init RichAds only if user level meets minimum (configurable via admin)
        const richAdsMinLevel = apiClient.configValue("richads_banner_min_level", 10);
        (window as any)._richAdsBannerMinLevel = richAdsMinLevel;
        const userLevel = (profile as any)?.absoluteLevelNum ?? 1;
        if ((window as any).initRichAds) (window as any).initRichAds(userLevel);
    }).catch(err => {
        console.warn("[TG] Server auth failed (offline mode):", err);
    });

    Promise.all([authPromise, configPromise]).finally(() => {
        startGame();
        apiClient.trackFunnelEvent("loading_done");
        removeLoader();
    });
} catch (err) {
    showTgError(err);
}
